import { Text, View } from '@react-pdf/renderer';
import { useMemo } from 'react';
import { COMMENTARY_TEXT } from '../../commentary/consts.ts';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { MarketConditionPageProps } from '../types.ts';
import { fontStyles, gridStyles } from '../styles.ts';
import { convertMarketAggregateDataToPDFTableColumns } from '../utils/convertMarketAggregateDataToPDFTableColumns.ts';
import { ExportCommentaryTable } from '../../commentary/export/ExportCommentaryTable.tsx';
import { FlexRow } from '../components/FlexRow.tsx';

export const MarketConditionPage = ({
  subjectAddress,
  commentaryV2,
  marketAggregateData,
  countyName,
  countyFIPS,
}: MarketConditionPageProps) => {
  const { title } = COMMENTARY_TEXT['market-conditions'];

  const monthlyDataRow1 = useMemo(() => {
    const entries = ((marketAggregateData?.monthlyData.length || 0) > 6 ?
      marketAggregateData?.monthlyData.slice(1, marketAggregateData?.monthlyData.length / 2 + 1) :
      marketAggregateData?.monthlyData) || [];

    return convertMarketAggregateDataToPDFTableColumns(entries);
  }, [marketAggregateData?.monthlyData]);

  const monthlyDataRow2 = useMemo(() => {
    const entries = ((marketAggregateData?.monthlyData.length || 0) > 6 ?
      marketAggregateData?.monthlyData.slice(marketAggregateData?.monthlyData.length / 2 + 1) :
      []) || [];
    return convertMarketAggregateDataToPDFTableColumns(entries);
  }, [marketAggregateData?.monthlyData]);

  let aggregateDataSourceText = '';
  switch (marketAggregateData?.geoType) {
    case 'zip':
      aggregateDataSourceText = `Zip Code ${marketAggregateData.geoId}`;
      break;
    case 'city':
      if (marketAggregateData?.geoId?.includes('City')) {
        aggregateDataSourceText = marketAggregateData.geoId;
        break;
      }
      aggregateDataSourceText = `City of ${marketAggregateData.geoId}`;
      break;
    case 'county':
      if (!countyName) {
        aggregateDataSourceText = 'County';
        break;
      }
      if (countyName?.includes('County')) {
        aggregateDataSourceText = countyName;
      } else {
        aggregateDataSourceText = `${countyName} County`;
      }

      if (countyFIPS) {
        aggregateDataSourceText += ` (FIPS: ${countyFIPS})`;
      }
      break;
    default:
      aggregateDataSourceText = 'Unknown';
  }
  return (
    <Tkv2PDFPage title={title} address={subjectAddress}>
      <View style={gridStyles.gridSection70}>
        <View style={gridStyles.row}>
          <View style={gridStyles.gridCol}>
            <Text style={fontStyles.h4Light}>Estimated Demand/Supply</Text>
          </View>
          <View style={gridStyles.gridCol}>
            <Text style={fontStyles.bodyLarge}>{commentaryV2.marketConditionsV2?.estimatedDemandSupply}</Text>
          </View>
        </View>
        <View style={gridStyles.row}>
          <View style={gridStyles.gridCol}>
            <Text style={fontStyles.h4Light}>Estimated Exposure Time</Text>
          </View>
          <View style={gridStyles.gridCol}>
            <Text style={fontStyles.bodyLarge}>{commentaryV2.marketConditionsV2?.estimatedExposureTime}</Text>
          </View>
        </View>
      </View>
      <View style={gridStyles.verticalSpace} />
      <View style={gridStyles.gridSection}>
        <Text style={fontStyles.h3}>Market Condition Commentary</Text>
        <Text style={fontStyles.body}>{commentaryV2.marketConditionsV2?.commentary}</Text>
      </View>
      <View style={gridStyles.verticalSpace} />
      <ExportCommentaryTable data={monthlyDataRow1} configKey='marketConditions' hideInnerContentBorders={true} />
      {monthlyDataRow2[0].length > 1 && <ExportCommentaryTable data={monthlyDataRow2} configKey='marketConditions' hideInnerContentBorders={true} />}
      <FlexRow>
        <Text style={fontStyles.bodyBold}>Source: </Text>
        <Text style={fontStyles.body}>{aggregateDataSourceText}</Text>
      </FlexRow>
    </Tkv2PDFPage>
  )
}
